import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnInit,
  ViewChild
} from '@angular/core';
import { AntipatternData, AntipatternStore, AntipatternTypeV2, AssetsStore, ViewStore } from '@coach-bot/data-access/shared';
import { BehaviorSubject, combineLatest, take } from 'rxjs';
import { FeedbackStages } from '../../enums';

@Component({
  selector: 'ap-antipattern-popup-v2',
  templateUrl: './antipattern-popup-v2.component.html',
  styleUrls: [
    '../../styles/antipattern-popup-feedback.scss',
    '../../styles/antipattern-popup.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AntipatternPopupV2Component implements OnInit {
  @ViewChild('videoPlayer') videoPlayer: ElementRef<HTMLVideoElement>;

  readonly assetsPath$: BehaviorSubject<string> = this.assetsStore.assetsPath$;
  readonly isFeedbackStageNotForm$ = new BehaviorSubject<boolean>(true);

  bannerClass: string;
  data: AntipatternData;
  feedbackStage: FeedbackStages = FeedbackStages.Initial;
  isAntipatternMedium: boolean = false;
  playButtonClicked: boolean = false;
  processedDescription: string;

  constructor(private antipatternStore: AntipatternStore, private assetsStore: AssetsStore, private viewStore: ViewStore) {}

  ngOnInit(): void {
    combineLatest([this.antipatternStore.antipatternData$, this.assetsPath$])
      .pipe(take(1))
      .subscribe(([antipatternData, assetsPath]: [AntipatternData, string]) => {
        this.data = antipatternData;
        this.processedDescription = this.processDescription(antipatternData.configuration?.description, assetsPath);
        this.bannerClass = this.getBannerClass();
        this.isAntipatternMedium = this.data.configuration.type === AntipatternTypeV2.AntipatternMedium;
      });
  }

  changeFeedbackStage(newFeedbackStage: FeedbackStages) {
    this.feedbackStage = newFeedbackStage;
    const isFeedbackStageNotForm: boolean = newFeedbackStage !== FeedbackStages.Form;
    if (isFeedbackStageNotForm !== this.isFeedbackStageNotForm$.value) {
      this.isFeedbackStageNotForm$.next(isFeedbackStageNotForm);
    }
  }

  onPlayButtonClicked() {
    this.playButtonClicked = true;
    this.videoPlayer.nativeElement.play();
  }

  close(): void {
    this.viewStore.setNotificationView(null);
  }

  private getBannerClass(): string {
    switch (this.data.configuration.type) {
      case AntipatternTypeV2.AntipatternHigh:
        return 'antipattern-high';
      case AntipatternTypeV2.AntipatternMedium:
        return 'antipattern-medium';
      case AntipatternTypeV2.Informational:
        return 'informational';
      case AntipatternTypeV2.FeelingStuck:
        return 'feeling-stuck';
      case AntipatternTypeV2.Motivational:
        return 'motivational';
      default:
        return '';
    }
  }

  private processDescription(description: string, assetsPath: string): string {
    const parser = new DOMParser();
    const parsedHTML = parser.parseFromString(description, 'text/html');
    const listItemElements = parsedHTML.querySelectorAll('ul li, ol li');
    const linkElements = parsedHTML.querySelectorAll('a');
    const orderedListElements = parsedHTML.querySelectorAll('ol');
    const unorderedListElements = parsedHTML.querySelectorAll('ul');
    orderedListElements.forEach((ol) => {
      ol.classList.add('coachbot-popup-all-ol');
    });
    unorderedListElements.forEach((ul) => {
      ul.classList.add('coachbot-popup-all-ul');
    });
    linkElements.forEach((link) => {
      const url = link.getAttribute('href');
      if (!url) {
        return;
      }

      let icon: string | null = null;
      if (url.indexOf('https://www.khanacademy.org/') === 0) {
        icon = `${assetsPath}/icons/khan-academy.svg`;
      } else if (url.indexOf('https://www.knowremath.com/') === 0) {
        icon = `${assetsPath}/icons/subtract.svg`;
      } else if (
        url.indexOf('https://www.youtube.com/') === 0 ||
        url.indexOf('https://youtu.be/') === 0
      ) {
        icon = `${assetsPath}/icons/youtube.svg`;
      } else if (url.indexOf('https://www.ixl.com/') === 0) {
        icon = `${assetsPath}/icons/IXL.svg`;
      }

      if (icon) {
        const img = document.createElement('img');
        img.setAttribute('src', icon);
        img.classList.add('link-icon');

        const div = document.createElement('div');
        div.classList.add('coachbot-all-popup-list-element');
        div.appendChild(img);
        div.appendChild(link.cloneNode(true));
        link.parentNode?.replaceChild(div, link);
      }
    });

    listItemElements.forEach((listItem) => {
      const listItemContent = document.createElement('span');
      listItemContent.style.flexGrow = '1';
      listItemContent.innerHTML = listItem.innerHTML;
      listItem.innerHTML = '';

      if (listItem.parentElement?.tagName.toLowerCase() === 'ol') {
        const index =
          Array.prototype.indexOf.call(
            listItem.parentElement?.children,
            listItem,
          ) + 1;
        const circle = document.createElement('span');
        circle.classList.add('coachbot-all-antipattern-popup-circle_counter');
        circle.textContent = index.toString();
        listItem.append(circle);
      }

      listItem.append(listItemContent);
      (listItem as HTMLElement).style.display = 'flex';
      (listItem as HTMLElement).style.gap = '8px';
    });

    return parsedHTML.body.innerHTML;
  }
}
